import React, { useState, useRef, useEffect, useCallback } from 'react'
import { X, Undo2, Copy, Scissors, ClipboardPaste, Trash2, FolderPlus, FilePlus, Upload, FolderUp, Link2, Pencil, FolderOpen, FolderSearch, Clock, ChevronRight, SquarePlus, LayoutGrid, Save, RotateCcw, Download } from 'lucide-react'
import type { RecentFile } from './useWorkspace'
import { useUndo, useCommandRegistryOptional, formatShortcut, ThemeToggle, type Command, PDAppIcon, Tooltip } from '@papan/core-ui/ui'
import type { ComponentType } from 'react'
import { getActiveTenantId } from '@papan/api-client'

// Default icons for common command IDs — editors don't need to import icons
const DEFAULT_CMD_ICONS: Record<string, ComponentType<{ size?: number }>> = {
  'edit.copy': Copy, 'edit.cut': Scissors, 'edit.paste': ClipboardPaste,
  'edit.delete': Trash2, 'edit.rename': Pencil,
  'file.save': Save, 'file.download': Download,
}

export interface RecentWorkspace {
  folderId: string
  name: string
  path: string
}

export interface EditActions {
  copy: () => void
  cut: () => void
  paste: () => void
  delete: () => void
  rename?: () => void
  getSelectedName: () => string | null
  getSelectedCount?: () => number
  getSelectedNames?: () => string[]
  getSelectedIds?: () => string[]
  getPasteTargetName?: () => string | null
  getDeleteLabel?: () => string
}

export interface FileActions {
  createFolder?: () => void
  newFile?: () => void
  /** Sets uploadTargetRef so <label htmlFor> triggers the right folder. Called synchronously before file dialog opens. */
  prepareUpload?: () => void
  linkFolder?: () => void
  openFilePicker: () => void
}

export type EditContext = 'explorer' | 'editor'

interface WorkspaceMenuBarProps {
  workspaceName: string
  workspacePath?: string
  onRename?: (newName: string) => void
  onClose: () => void
  editActions?: EditActions | null
  fileActions?: FileActions | null
  editContext: EditContext
  recentFiles?: RecentFile[]
  onOpenRecentFile?: (fileId: string) => void
  /** Container kind of the open workspace — drives the menu label ("Project" vs "PDApp"). */
  entityKind?: 'project' | 'miniapp'
  onNewProject?: () => void
  onNewMiniapp?: () => void
  /** Reveal this workspace folder in the Drive. */
  onRevealInDrive?: () => void
  onOpenWorkspace?: () => void
  recentWorkspaces?: RecentWorkspace[]
  onOpenRecentWorkspace?: (folderId: string) => void
  currentWorkspaceFolderId?: string | null
  onSave?: () => void
  onSaveAll?: () => void
  onSaveAs?: () => void
  onDiscard?: () => void
  canSave?: boolean
  canSaveAll?: boolean
  canSaveAs?: boolean
  canDiscard?: boolean
  onDownload?: () => void
  canDownload?: boolean
  onSetAIAccess?: (ids: string[], permission: 'hidden' | 'r') => void
  onClearAIAccess?: (ids: string[]) => void
  getSelectedAIAccess?: () => 'hidden' | 'r' | undefined
  getSelectedAllFolders?: () => boolean
}

const CLIPBOARD_KEY = 'papan-clipboard'

function truncName(name: string, max = 20): string {
  return name.length > max ? name.slice(0, max - 1) + '\u2026' : name
}

interface ClipboardData {
  items: { id: string; name: string; isFolder: boolean; parentId: string | null }[]
  isCut: boolean
  tenantId?: string
}

type MenuName = 'Workspace' | 'File' | 'Edit' | 'View' | 'Help'

/**
 * The menus that RENDER. `View` and `Help` are deliberately absent.
 *
 * Neither has ever had a dropdown — grep this file for `openMenu === 'View'`
 * and there is nothing to find — so both were buttons that opened an empty
 * space. A menu that visibly does nothing reads as broken, and a reader cannot
 * tell "not built yet" from "built and failing"; leaving them out says the same
 * thing honestly until there is something to put in them.
 *
 * They stay in `MenuName` so restoring one is a single edit here rather than a
 * type change, and so the dropdown-less state stays visible in the type.
 */
const allMenus: MenuName[] = ['Workspace', 'File', 'Edit']

function WorkspaceNameWithTooltip({ name, path, onDoubleClick }: { name: string; path?: string; onDoubleClick?: () => void }) {
  return (
    <Tooltip content={path} always className="truncate max-w-[240px] text-sm font-medium themed-text">
      <span
        onDoubleClick={onDoubleClick}
        className="cursor-default select-none"
      >
        {name}
      </span>
    </Tooltip>
  )
}

export function WorkspaceMenuBar({ workspaceName, workspacePath, onRename, onClose, editActions, fileActions, editContext, recentFiles, onOpenRecentFile, entityKind = 'project', onNewProject, onNewMiniapp, onRevealInDrive, onOpenWorkspace, recentWorkspaces, onOpenRecentWorkspace, currentWorkspaceFolderId, onSave, onSaveAll, onSaveAs, onDiscard, canSave, canSaveAll, canSaveAs, canDiscard, onDownload, canDownload, onSetAIAccess: _onSetAIAccess, onClearAIAccess: _onClearAIAccess, getSelectedAIAccess: _getSelectedAIAccess, getSelectedAllFolders: _getSelectedAllFolders }: WorkspaceMenuBarProps) {
  // The first menu is titled after the open container's kind. Internally it
  // stays keyed as 'Workspace' (open/close state), only the displayed label
  // and the entity-scoped action labels change.
  const entityLabel = entityKind === 'miniapp' ? 'PDApp' : 'Project'
  const [isRenaming, setIsRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState(workspaceName)
  const inputRef = useRef<HTMLInputElement>(null)
  const [openMenu, setOpenMenu] = useState<string | null>(null)
  const menuBarRef = useRef<HTMLDivElement>(null)
  const undo = useUndo()
  // Command registry — optional, reads commands when Edit menu opens
  const registry = useCommandRegistryOptional()
  const registryRef = useRef(registry)
  registryRef.current = registry

  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
      setRenameValue(workspaceName)
    }
  }, [isRenaming, workspaceName])

  const commitRename = () => {
    setIsRenaming(false)
    const trimmed = renameValue.trim()
    if (trimmed && trimmed !== workspaceName) {
      onRename?.(trimmed)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commitRename()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setIsRenaming(false)
    }
  }

  const handleMenuClick = useCallback((name: string) => {
    setOpenMenu(prev => prev === name ? null : name)
  }, [])

  const handleMenuHover = useCallback((name: string) => {
    if (openMenu) setOpenMenu(name)
  }, [openMenu])

  // Close menu on outside click — use capture phase so canvas-based editors
  // (like Univer) that stopPropagation on mousedown can't block it
  useEffect(() => {
    if (!openMenu) return
    const handler = (e: MouseEvent) => {
      if (menuBarRef.current && !menuBarRef.current.contains(e.target as Node)) {
        setOpenMenu(null)
      }
    }
    // Listen on both mousedown and click (capture) to catch all cases
    document.addEventListener('mousedown', handler, true)
    document.addEventListener('click', handler, true)
    return () => {
      document.removeEventListener('mousedown', handler, true)
      document.removeEventListener('click', handler, true)
    }
  }, [openMenu])

  // Close menu when hovering into the taskbar (activity bar)
  useEffect(() => {
    if (!openMenu) return
    const taskbar = document.querySelector('[data-taskbar]')
    if (!taskbar) return
    const handler = () => setOpenMenu(null)
    taskbar.addEventListener('mouseenter', handler)
    return () => taskbar.removeEventListener('mouseenter', handler)
  }, [openMenu])

  // Close menu on Escape
  useEffect(() => {
    if (!openMenu) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenMenu(null)
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [openMenu])

  // Compute contextual labels when Edit dropdown is open
  // (fresh read each render — the dropdown is transient so this is always up-to-date)
  const isEditorCtx = editContext === 'editor'
  const selectedCount = openMenu === 'Edit' && !isEditorCtx ? editActions?.getSelectedCount?.() ?? 0 : 0
  const selectedName = openMenu === 'Edit' && !isEditorCtx ? editActions?.getSelectedName() ?? null : null
  const clipboardData: ClipboardData | null = openMenu === 'Edit' && !isEditorCtx ? (() => {
    try {
      const raw = localStorage.getItem(CLIPBOARD_KEY)
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  })() : null



  const deleteLabel = openMenu === 'Edit' && !isEditorCtx
    ? editActions?.getDeleteLabel?.() ?? 'Delete'
    : 'Delete'

  // In editor context, clipboard operations are always available
  const canCopy = isEditorCtx || selectedCount > 0
  const canCut = isEditorCtx || selectedCount > 0
  // (#752) The clipboard survives a tenant switch; the explorer pastes within one
  // tenant only (cross-tenant copy lives in the drive), so don't offer it.
  const clipboardForeign = !!clipboardData?.tenantId && clipboardData.tenantId !== getActiveTenantId()
  const canPaste = isEditorCtx || (!!clipboardData && !clipboardForeign)
  const canDelete = isEditorCtx || selectedCount > 0

  // Label suffix for multi-selection
  const selLabel = selectedCount > 1 ? `${selectedCount} items` : selectedName ? `"${truncName(selectedName)}"` : null

  const isMac = navigator.platform.includes('Mac')
  const mod = isMac ? '⌘' : 'Ctrl+'

  // Frost-glass menu kit (ws-menu / ws-menu-item) — same look as the drive
  // context menu + editor menus, replacing the pre-redesign flat theme (#228).
  const menuItemClass = (enabled: boolean) =>
    `ws-menu-item ${enabled ? 'cursor-pointer' : 'opacity-50 cursor-default'}`

  return (
    <div
      ref={menuBarRef}
      data-workspace-menubar
      className="ws-topbar"
      onClick={(e) => {
        // Close menu when clicking non-menu areas of the bar (spacer, name, close button)
        if (openMenu && !(e.target as HTMLElement).closest('[data-menu-item]')) setOpenMenu(null)
      }}
    >
      {/* Workspace name — double-click to rename */}
      {isRenaming ? (
        <input
          ref={inputRef}
          value={renameValue}
          onChange={e => setRenameValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={commitRename}
          className="text-sm font-medium bg-transparent border themed-border rounded px-1.5 py-0 themed-text outline-none focus:border-brand-500 min-w-[120px] max-w-[240px]"
          onClick={e => e.stopPropagation()}
        />
      ) : (
        <WorkspaceNameWithTooltip
          name={workspaceName || 'Untitled'}
          path={workspacePath}
          onDoubleClick={onRename ? () => setIsRenaming(true) : undefined}
        />
      )}

      {/* Separator */}
      <div className="w-px h-4 themed-border-secondary mx-2 opacity-30" />

      {/* Menu items */}
      {allMenus.map(name => (
        <div key={name} data-menu-item className="relative">
          <button
            onClick={() => handleMenuClick(name)}
            onMouseEnter={() => handleMenuHover(name)}
            className={`px-2 py-0.5 text-xs rounded transition-colors ${
              openMenu === name
                ? 'themed-bg-tertiary themed-text'
                : 'themed-text-muted hover:themed-text hover:themed-bg-tertiary'
            }`}
          >
            {name === 'Workspace' ? entityLabel : name}
          </button>

          {/* Workspace dropdown */}
          {name === 'Workspace' && openMenu === 'Workspace' && (
            <div className="absolute top-full left-0 mt-0.5 ws-menu z-50 min-w-[260px]">
              <button
                onClick={() => { onNewProject?.(); setOpenMenu(null) }}
                className={menuItemClass(!!onNewProject)}
                disabled={!onNewProject}
              >
                <SquarePlus size={13} />
                <span>New Project</span>
              </button>
              <button
                onClick={() => { onNewMiniapp?.(); setOpenMenu(null) }}
                className={menuItemClass(!!onNewMiniapp)}
                disabled={!onNewMiniapp}
              >
                <PDAppIcon size={13} />
                <span>New PDApp</span>
              </button>
              <button
                onClick={() => { onOpenWorkspace?.(); setOpenMenu(null) }}
                className={menuItemClass(!!onOpenWorkspace)}
                disabled={!onOpenWorkspace}
              >
                <LayoutGrid size={13} />
                <span>Open Project...</span>
              </button>

              {/* Open Recent Workspace submenu */}
              {recentWorkspaces && recentWorkspaces.length > 0 && (
                <div className="relative group/recentws">
                  <button
                    className={`${menuItemClass(true)} justify-between`}
                    onClick={e => e.stopPropagation()}
                  >
                    <span className="flex items-center gap-2">
                      <Clock size={13} />
                      <span>Open Recent</span>
                    </span>
                    <ChevronRight size={12} className="ml-auto" />
                  </button>
                  <div className="absolute left-full top-0 pl-1 hidden group-hover/recentws:block">
                    <div className="ws-menu z-50 min-w-[240px]">
                    {recentWorkspaces.filter(rw => rw.folderId !== currentWorkspaceFolderId).map(rw => (
                      <button
                        key={rw.folderId}
                        onClick={() => { onOpenRecentWorkspace?.(rw.folderId); setOpenMenu(null) }}
                        className={`${menuItemClass(true)} !items-start`}
                      >
                        <LayoutGrid size={14} className="themed-text-muted shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="truncate text-xs">{rw.name}</div>
                          <div className="truncate text-[10px] themed-text-muted">{rw.path}</div>
                        </div>
                      </button>
                    ))}
                    {recentWorkspaces.filter(rw => rw.folderId !== currentWorkspaceFolderId).length === 0 && (
                      <div className="px-3 py-1.5 text-xs themed-text-muted">No other workspaces</div>
                    )}
                    </div>
                  </div>
                </div>
              )}

              <div className="ws-menu-sep" />

              <button
                onClick={() => { onRevealInDrive?.(); setOpenMenu(null) }}
                className={menuItemClass(!!onRevealInDrive)}
                disabled={!onRevealInDrive}
              >
                <FolderSearch size={13} />
                <span>Reveal in Drive</span>
              </button>
              <button
                onClick={() => { setIsRenaming(true); setOpenMenu(null) }}
                className={menuItemClass(!!onRename)}
                disabled={!onRename}
              >
                <Pencil size={13} />
                <span>Rename {entityLabel}</span>
              </button>

              <div className="ws-menu-sep" />

              <button
                onClick={() => { onClose(); setOpenMenu(null) }}
                className={menuItemClass(true)}
              >
                <X size={13} />
                <span>Close {entityLabel}</span>
              </button>
            </div>
          )}

          {/* File dropdown */}
          {name === 'File' && openMenu === 'File' && (
            <div className="absolute top-full left-0 mt-0.5 ws-menu z-50 min-w-[200px]">
              <button
                onClick={() => { fileActions?.newFile?.(); setOpenMenu(null) }}
                className={menuItemClass(!!fileActions?.newFile)}
                disabled={!fileActions?.newFile}
              >
                <FilePlus size={13} />
                <span>New Text File</span>
              </button>
              <button
                onClick={() => { fileActions?.createFolder?.(); setOpenMenu(null) }}
                className={menuItemClass(!!fileActions?.createFolder)}
                disabled={!fileActions?.createFolder}
              >
                <FolderPlus size={13} />
                <span>New Folder</span>
              </button>

              <div className="ws-menu-sep" />

              <button
                onClick={() => { fileActions?.openFilePicker(); setOpenMenu(null) }}
                className={menuItemClass(!!fileActions)}
                disabled={!fileActions}
              >
                <FolderOpen size={13} />
                <span>Open...</span>
              </button>

              {/* Open Recent submenu */}
              {recentFiles && recentFiles.length > 0 && (
                <div className="relative group/recent">
                  <button
                    className={`${menuItemClass(true)} justify-between`}
                    onClick={e => e.stopPropagation()}
                  >
                    <span className="flex items-center gap-2">
                      <Clock size={13} />
                      <span>Open Recent</span>
                    </span>
                    <ChevronRight size={12} className="ml-auto" />
                  </button>
                  <div className="absolute left-full top-0 pl-1 hidden group-hover/recent:block">
                    <div className="ws-menu z-50 min-w-[200px]">
                    {recentFiles.map(rf => (
                      <button
                        key={rf.fileId}
                        onClick={() => { onOpenRecentFile?.(rf.fileId); setOpenMenu(null) }}
                        className={menuItemClass(true)}
                      >
                        <span className="truncate">{rf.name}</span>
                      </button>
                    ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="ws-menu-sep" />

              <button
                onClick={() => { onSave?.(); setOpenMenu(null) }}
                disabled={!canSave}
                className={menuItemClass(!!canSave)}
              >
                <Save size={13} />
                <span className="flex-1">Save</span>
                <span className="ml-auto text-[10px] themed-text-muted shrink-0">{mod}S</span>
              </button>
              <button
                onClick={() => { onSaveAll?.(); setOpenMenu(null) }}
                disabled={!canSaveAll}
                className={menuItemClass(!!canSaveAll)}
              >
                <Save size={13} />
                <span className="flex-1">Save All</span>
                <span className="ml-auto text-[10px] themed-text-muted shrink-0">{mod}⇧S</span>
              </button>
              <button
                onClick={() => { onSaveAs?.(); setOpenMenu(null) }}
                disabled={!canSaveAs}
                className={menuItemClass(!!canSaveAs)}
              >
                <Save size={13} />
                <span>Save As...</span>
              </button>
              <button
                onClick={() => { onDiscard?.(); setOpenMenu(null) }}
                disabled={!canDiscard}
                className={`${menuItemClass(!!canDiscard)} ${canDiscard ? '!text-red-400' : ''}`}
              >
                <RotateCcw size={13} />
                <span>Discard Changes</span>
              </button>
              <button
                onClick={() => { onDownload?.(); setOpenMenu(null) }}
                disabled={!canDownload}
                className={menuItemClass(!!canDownload)}
              >
                <Download size={13} />
                <span>Download</span>
              </button>

              <div className="ws-menu-sep" />

              <label
                htmlFor="workspace-file-upload"
                onClick={() => { fileActions?.prepareUpload?.(); requestAnimationFrame(() => setOpenMenu(null)) }}
                className={`${menuItemClass(!!fileActions?.prepareUpload)} ${fileActions?.prepareUpload ? 'cursor-pointer' : 'opacity-50 pointer-events-none'}`}
              >
                <Upload size={13} />
                <span>Upload files...</span>
              </label>
              <label
                htmlFor="workspace-folder-upload"
                onClick={() => { fileActions?.prepareUpload?.(); requestAnimationFrame(() => setOpenMenu(null)) }}
                className={`${menuItemClass(!!fileActions?.prepareUpload)} ${fileActions?.prepareUpload ? 'cursor-pointer' : 'opacity-50 pointer-events-none'}`}
              >
                <FolderUp size={13} />
                <span>Upload folder...</span>
              </label>
              <button
                onClick={() => { fileActions?.linkFolder?.(); setOpenMenu(null) }}
                className={menuItemClass(!!fileActions?.linkFolder)}
                disabled={!fileActions?.linkFolder}
              >
                <Link2 size={13} />
                <span>Link Drive Folder...</span>
              </button>
            </div>
          )}

          {/* Edit dropdown */}
          {name === 'Edit' && openMenu === 'Edit' && (() => {
            const registry = registryRef.current
            const editCommands = registry ? registry.getMenuCommands('edit') : []

            // Render undo/redo (always from the undo hook — stays separate from command registry)
            const renderUndoRedo = () => (
              <>
                <button
                  onClick={() => { undo.undo(); setOpenMenu(null) }}
                  disabled={!undo.canUndo}
                  className={menuItemClass(undo.canUndo)}
                >
                  <Undo2 size={13} />
                  <span className="truncate">{undo.undoLabel ? `Undo ${undo.undoLabel}` : 'Undo'}</span>
                  <span className="ml-auto text-[10px] themed-text-muted shrink-0">{mod}Z</span>
                </button>
                <button
                  onClick={() => { undo.redo(); setOpenMenu(null) }}
                  disabled={!undo.canRedo}
                  className={menuItemClass(undo.canRedo)}
                >
                  <RotateCcw size={13} className="scale-x-[-1]" />
                  <span className="truncate">{undo.redoLabel ? `Redo ${undo.redoLabel}` : 'Redo'}</span>
                  <span className="ml-auto text-[10px] themed-text-muted shrink-0">{mod}⇧Z</span>
                </button>
                {editCommands.length > 0 && <div className="ws-menu-sep" />}
              </>
            )

            // Render a single command
            const renderCommand = (cmd: Command) => {
              if (cmd.submenu) {
                return (
                  <div key={cmd.id} className="relative group/sub">
                    <button className={`${menuItemClass(cmd.enabled)} justify-between`} onClick={e => e.stopPropagation()}>
                      <span className="flex items-center gap-2">
                        {(() => { const Icon = cmd.icon || DEFAULT_CMD_ICONS[cmd.id]; return Icon ? <Icon size={13} /> : null })()}
                        <span className="truncate">{cmd.label}</span>
                      </span>
                      <ChevronRight size={12} className="ml-auto" />
                    </button>
                    <div className="absolute left-full top-0 pl-1 hidden group-hover/sub:block">
                      <div className="ws-menu z-50 min-w-[150px]">
                        {cmd.submenu.map(sub => (
                          <button
                            key={sub.id}
                            onClick={() => { if (sub.enabled) { sub.execute(); setOpenMenu(null) } }}
                            disabled={!sub.enabled}
                            className={sub.danger ? `w-full px-3 py-1.5 text-left text-xs flex items-center gap-2 ${sub.enabled ? 'text-red-400 hover:themed-bg-secondary' : 'themed-text-muted opacity-50 cursor-default'}` : menuItemClass(sub.enabled)}
                          >
                            {sub.icon && <sub.icon size={13} />}
                            <span className="truncate">{sub.label}</span>
                            {sub.shortcut && <span className="ml-auto text-[10px] themed-text-muted shrink-0">{formatShortcut(sub.shortcut)}</span>}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )
              }
              return (
                <React.Fragment key={cmd.id}>
                  {cmd.separator === 'before' && <div className="ws-menu-sep" />}
                  <button
                    onClick={() => { if (cmd.enabled) { cmd.execute(); setOpenMenu(null) } }}
                    disabled={!cmd.enabled}
                    className={cmd.danger
                      ? `w-full px-3 py-1.5 text-left text-xs flex items-center gap-2 ${cmd.enabled ? 'text-red-400 hover:themed-bg-secondary' : 'themed-text-muted opacity-50 cursor-default'}`
                      : menuItemClass(cmd.enabled)
                    }
                  >
                    {(() => { const Icon = cmd.icon || DEFAULT_CMD_ICONS[cmd.id]; return Icon ? <Icon size={13} /> : null })()}
                    <span className="truncate">{cmd.label}</span>
                    {cmd.shortcut && <span className="ml-auto text-[10px] themed-text-muted shrink-0">{formatShortcut(cmd.shortcut)}</span>}
                  </button>
                  {cmd.separator === 'after' && <div className="ws-menu-sep" />}
                </React.Fragment>
              )
            }

            return (
              <div className="absolute top-full left-0 mt-0.5 ws-menu z-50 min-w-[200px]">
                {renderUndoRedo()}
                {editCommands.length > 0
                  ? editCommands.map(renderCommand)
                  : <>
                      {/* Fallback: old-style edit actions (during migration) */}
                      <button onClick={() => { editActions?.copy(); setOpenMenu(null) }} disabled={!canCopy} className={menuItemClass(canCopy)}>
                        <Copy size={13} /><span className="truncate">{selLabel ? `Copy ${selLabel}` : 'Copy'}</span>
                        <span className="ml-auto text-[10px] themed-text-muted shrink-0">{mod}C</span>
                      </button>
                      <button onClick={() => { editActions?.cut(); setOpenMenu(null) }} disabled={!canCut} className={menuItemClass(canCut)}>
                        <Scissors size={13} /><span className="truncate">{selLabel ? `Cut ${selLabel}` : 'Cut'}</span>
                        <span className="ml-auto text-[10px] themed-text-muted shrink-0">{mod}X</span>
                      </button>
                      <button onClick={() => { editActions?.paste(); setOpenMenu(null) }} disabled={!canPaste} className={menuItemClass(canPaste)}>
                        <ClipboardPaste size={13} /><span className="truncate">Paste</span>
                        <span className="ml-auto text-[10px] themed-text-muted shrink-0">{mod}V</span>
                      </button>
                      <div className="ws-menu-sep" />
                      <button onClick={() => { editActions?.delete(); setOpenMenu(null) }} disabled={!canDelete}
                        className={`w-full px-3 py-1.5 text-left text-xs flex items-center gap-2 ${canDelete ? 'text-red-400 hover:themed-bg-secondary' : 'themed-text-muted opacity-50 cursor-default'}`}>
                        <Trash2 size={13} /><span className="truncate">{selLabel ? `${deleteLabel} ${selLabel}` : deleteLabel}</span>
                        <span className="ml-auto text-[10px] themed-text-muted shrink-0">⌫</span>
                      </button>
                    </>
                }
              </div>
            )
          })()}
        </div>
      ))}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Dark / dim / light toggle */}
      <ThemeToggle />

      {/* Close workspace — macOS-style red traffic-light button (× on hover).
          Translucent at rest, solidifies on hover; extra right margin keeps it
          off the bar's edge. */}
      <button
        onClick={onClose}
        className="group/close ml-2 mr-1.5 w-3.5 h-3.5 rounded-full bg-[#ff5f57]/45 hover:bg-[#ff5f57] border border-black/10 flex items-center justify-center transition-colors"
        title="Close workspace"
        aria-label="Close workspace"
      >
        <X size={9} strokeWidth={3} className="text-black/60 opacity-0 group-hover/close:opacity-100 transition-opacity" />
      </button>
    </div>
  )
}
